import { useEffect, useState } from 'react';
import { auth, db } from '@/integrations/firebase/client';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Navigation } from './Navigation';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { useTheme } from 'next-themes';
import { UsageModal } from '@/components/studio/UsageModal';
import { AddCreditsModal } from '@/components/studio/AddCreditsModal';
import { BillingModal } from '@/components/studio/BillingModal';


export const Header = () => {
  const { i18n, t } = useTranslation();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [credits, setCredits] = useState<number | null>(null);
  const [darkMode, setDarkMode] = useState<boolean | null>(null);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  
  const { setTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  useEffect(() => {
    if (!user?.uid) {
      setCredits(null);
      return;
    }
    const ref = doc(db, 'users', user.uid, 'settings', 'credits');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const total = snap.data()?.total;
        setCredits(typeof total === 'number' ? total : 0);
      },
      (error) => {
        console.error('Credits snapshot error:', error);
      }
    );
    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setDarkMode(null);
      return;
    }
    const ref = doc(db, 'users', user.uid, 'settings', 'preferences');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const dm = snap.data()?.darkMode;
        if (typeof dm === 'boolean') {
          setDarkMode(dm);
          setTheme(dm ? 'dark' : 'light');
        }
      },
      (error) => {
        console.error('Preferences snapshot error:', error);
      }
    );
    return unsub;
  }, [user?.uid, setTheme]);
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <header className="w-full border-b sticky top-0 z-40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container flex h-16 items-center justify-between">
        <a href="/" className="flex items-center">
          <img src="/logo-big.png" alt={t('app.title')} className="h-10" />
        </a>
        
        <div className="flex items-center gap-4">
          <Navigation />
          
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-3 px-2 hover:bg-accent">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.photoURL ?? undefined} alt={user.displayName ?? user.email ?? t('app.user')} />
                        <AvatarFallback>
                          {(user.displayName ?? user.email ?? t('app.user')).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {credits !== null && (
                        <span className="text-sm font-medium text-muted-foreground">
                          {t('header.credits', { count: Number(credits.toFixed(1)) })}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 z-50 bg-popover border shadow-md">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium leading-none">{user.displayName || user.email}</span>
                        {user.email && (
                          <span className="text-xs text-muted-foreground">{user.email}</span>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled>
                      <div className="flex w-full items-center justify-between">
                        <span>{t('header.creditsLabel')}</span>
                        <span className="font-semibold">{credits ? credits.toFixed(1) : '—'}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem onClick={() => setShowUsageModal(true)}>
                      {t('header.usage')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowAddCreditsModal(true)}>
                      {t('header.addCredits')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowBillingModal(true)}>
                      {t('header.billing')}
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>{t('header.language')}</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuRadioGroup
                            value={i18n.language}
                            onValueChange={(v) => i18n.changeLanguage(v)}
                          >
                            {Object.keys(i18n.options.resources || {}).map((lang) => (
                              <DropdownMenuRadioItem key={lang} value={lang}>
                                {t(`languages.${lang}`)}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuItem onSelect={() => {
                      const newDarkMode = !darkMode;
                      setDarkMode(newDarkMode);
                      setTheme(newDarkMode ? 'dark' : 'light');
                      if (user?.uid) {
                        setDoc(doc(db, 'users', user.uid, 'settings', 'preferences'), { darkMode: newDarkMode }, { merge: true }).catch(error => {
                          console.error('Error saving theme preference:', error);
                        });
                      }
                    }}>
                      <div className="flex w-full items-center justify-between">
                        <span>{t('header.darkMode')}</span>
                        <Switch
                          checked={!!darkMode}
                        />
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      {t('nav.signout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <select
                  aria-label={t('header.language')}
                  className="hidden md:block bg-secondary text-secondary-foreground rounded-md px-2 py-1 text-sm"
                  value={i18n.language}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                >
                  <option value="es">{t('languages.es')}</option>
                  <option value="en">{t('languages.en')}</option>
                </select>
                <Button onClick={() => navigate('/auth')}>
                  {t('nav.signin')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Modals */}
      <UsageModal 
        isOpen={showUsageModal} 
        onOpenChange={setShowUsageModal}
        onAddCreditsClick={() => {
          setShowUsageModal(false);
          setShowAddCreditsModal(true);
        }}
      />
      <AddCreditsModal 
        isOpen={showAddCreditsModal} 
        onOpenChange={setShowAddCreditsModal}
      />
      <BillingModal 
        isOpen={showBillingModal} 
        onOpenChange={setShowBillingModal}
      />
      
    </header>
  );
};
